import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { MdDelete, MdKeyboardArrowRight } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type { MouseEvent, ReactElement } from 'react'

import { ReportedEntity } from './ReportEntity'

import { ReportCountBadge, ReportStatusBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import DetailSection from '~/components/DetailSection'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import DataTable, { DataTableItem } from '~/components/Tables/DataTable'
import UserCard from '~/components/UserCard'
import { useAppSummary } from '~/providers/AppSummary'
import { keys as reportsKeys, useGetReportByEntity, useProcessBatchReportMutation } from '~/queries/reports'
import { useUserDetailsQuery } from '~/queries/users'
import { REPORT_STATUS } from '~/types/report'
import { isProduction } from '~/utils/env'
import { handleGenericError } from '~/utils/error'
import { capitalize } from '~/utils/string'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const noOp = () => null

const PanelContent = ({ entityId }: { entityId: string }): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { appKey, deviceType } = useAppSummary()

  const [isRemoveModalOpen, setRemoveModalState] = useState(false)

  const [isIgnoreModalOpen, setIgnoreModalState] = useState(false)

  /** Queries & Mutations */

  const { mutateAsync: processBatchReport } = useProcessBatchReportMutation()

  const { data: report, isError, isLoading } = useGetReportByEntity({ appKey, deviceType, entityId })

  const { data: reviewer } = useUserDetailsQuery(report?.reviewed_by)

  /** Handlers */

  const handleRedirect = (path: string) => (evt: MouseEvent<HTMLDivElement>) => {
    if (report) {
      if (evt.metaKey) {
        window.open(path, '_blank')
      } else {
        navigate(path)
      }
    }
  }

  const handleRemoveModalState = (isOpen: boolean) => () => setRemoveModalState(isOpen)

  const handleIgnoreModalState = (isOpen: boolean) => () => setIgnoreModalState(isOpen)

  const handleRemoveConfirm = async () => {
    if (report) {
      try {
        await processBatchReport({
          appKey,
          decision: 'delete',
          deviceType,
          reportIds: [report.reported_entity_id],
        })
        await queryClient.invalidateQueries({ queryKey: reportsKeys.all })
        displayToast(`Reported ${report.reported_entity_type} was successfully removed`, 'success')
      } catch (e) {
        handleGenericError(e)
      } finally {
        setRemoveModalState(false)
      }
    }
  }

  const handleIgnoreConfirm = async () => {
    if (report) {
      try {
        await processBatchReport({
          appKey,
          decision: 'no-action',
          deviceType,
          reportIds: [report.reported_entity_id],
        })
        await queryClient.invalidateQueries({ queryKey: reportsKeys.all })
        displayToast('Report was successfully ignored', 'success')
      } catch (e) {
        handleGenericError(e)
      } finally {
        setIgnoreModalState(false)
      }
    }
  }

  /** Render */

  return (
    <>
      <div className="reports-panel-content">
        {isError ? (
          <div>No information found for this report</div>
        ) : isLoading ? (
          <MwMLoader category="item" />
        ) : report ? (
          <>
            <div className="reports-panel-content__header">
              <div className="reports-panel-content__header__report-infos">
                <div className="title-container">
                  <div className="title-container__title">Report overview</div>
                  {report.status === REPORT_STATUS.PENDING ? (
                    <div className="badge">
                      <ReportStatusBadge value={report.status} />
                    </div>
                  ) : null}
                </div>
                <div className="reports-panel-content__header__report-infos__creation-date">
                  {formatDate(report.data.created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                </div>
                <div className="reports-panel-content__header__report-infos__actions-container">
                  {report.data.profile_id ? (
                    <>
                      <LinkButton
                        backgroundColor="light"
                        label="Show user account"
                        onClick={handleRedirect(
                          `/accounts/${report.data.profile_id}/${isProduction() ? 'prod' : 'dev'}`
                        )}
                        type="internal"
                      />
                      <LinkButton
                        backgroundColor="light"
                        label="Show posts"
                        onClick={handleRedirect(`/profiles/${report.data.profile_id}/posts`)}
                        type="internal"
                      />
                      <LinkButton
                        backgroundColor="light"
                        label="Show projects"
                        onClick={handleRedirect(`/profiles/${report.data.profile_id}/projects`)}
                        type="internal"
                      />
                    </>
                  ) : null}
                </div>
              </div>
              <div className="reports-panel-content__header__actions-container">
                {report.status === REPORT_STATUS.PENDING ? (
                  <>
                    <Button onClick={handleRemoveModalState(true)}>Remove</Button>
                    <Button onClick={handleIgnoreModalState(true)} outlined>
                      Ignore
                    </Button>
                  </>
                ) : (
                  <div className="reports-panel-content__header__actions-container__reporter-container">
                    <ReportStatusBadge value={report.status} />
                    {reviewer ? (
                      <>
                        <span className="reports-panel-content__header__actions-container__reporter-container__by">
                          by
                        </span>
                        <UserCard
                          name={reviewer.name}
                          picture={reviewer.picture}
                          subInfo={formatDate(report.reviewed_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                        />
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <DetailSection
              className="report-panel-details-section"
              title={
                <>
                  <span>{capitalize(report.reported_entity_type)} details</span>{' '}
                  <div className="badge">
                    <ReportCountBadge value={report.reports_count} />
                  </div>
                </>
              }
            >
              <ReportedEntity report={report} />
            </DetailSection>
            <DetailSection className="report-panel-details-section" title="Profile details">
              <DataTable className="report-panel-data-table">
                <DataTableItem label="Name" value={report.data.username || '-'} />
                <DataTableItem label="ID" value={report.data.profile_id || '-'} />
                <DataTableItem
                  label="Created at"
                  value={formatDate(report.data.created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                />
              </DataTable>
            </DetailSection>
          </>
        ) : null}
      </div>
      <ModalDialog
        btnConfirm={<Button onClick={handleRemoveConfirm}>Remove</Button>}
        icon={<MdDelete color="#ca3423" size={65} />}
        isOpen={isRemoveModalOpen}
        mainText={`Do you really want to remove this ${report?.reported_entity_type}?`}
        onClose={handleRemoveModalState(false)}
        onConfirm={noOp}
      />
      <ModalDialog
        btnConfirm={<Button onClick={handleIgnoreConfirm}>Ignore</Button>}
        icon={<MdKeyboardArrowRight color="#06703D" size={65} />}
        isOpen={isIgnoreModalOpen}
        mainText="Do you really want to ignore this report?"
        onClose={handleIgnoreModalState(false)}
        onConfirm={noOp}
      />
    </>
  )
}

export default PanelContent
